/*
 * AbstractIcons - the icon language for every public/landing page.
 *
 * The marketing pages used to lean on lucide pictograms: a literal telephone
 * for "we call you", a literal graduation cap for "educators", a literal rocket
 * for "launch". Read as a set they looked like clip-art, and at 20px the detail
 * turned to mush. These replace them with a single abstract, geometric
 * vocabulary - rings, arcs, hexagons, dot rows, offset planes - so the whole
 * site reads as one brand rather than a bag of borrowed glyphs.
 *
 * Rules of the language (keep new marks inside them):
 *   - 24x24 grid, everything between 3 and 21 so optical weight matches.
 *   - Stroke only, currentColor, round caps and joins. Colour comes from the
 *     wrapper (.ic, .l7-ic, an inline `color`), exactly as it did for lucide.
 *   - Four or five elements maximum. These are read at 17-26px.
 *   - Primitives, not portraits. A calendar is a plane plus a dot row; a
 *     partnership is two interlocking hooks. No handles, spouts, or faces.
 *
 * The API mirrors lucide (size, strokeWidth, className, style, color) so these
 * drop straight into the old call sites. Where a file typed a prop as
 * `LucideIcon`, use the `AbIcon` type exported here instead.
 */
import type { CSSProperties, ReactNode } from 'react';

export type AbIconProps = {
  size?: number | string;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
  /** Overrides currentColor, for the few call sites that paint inline. */
  color?: string;
  /** Drops the duotone body — a pure outline, for very small or dense uses. */
  flat?: boolean;
};

/** A component from this module. Replaces `LucideIcon` in landing-page props. */
export type AbIcon = (props: AbIconProps) => ReactNode;

function Ab({ size = 22, strokeWidth = 1.7, className, style, color, flat, children }: AbIconProps & { flat?: boolean; children: ReactNode }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" fillOpacity={flat ? 0 : undefined}
      stroke={color || 'currentColor'} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round"
      className={className} style={style} aria-hidden="true" focusable="false"
    >{children}</svg>
  );
}

/* ── Duotone ─────────────────────────────────────────────────────────────────
   A pure outline reads as a wireframe: at 20px there is no mass to it, and eight
   of them in a grid look like a schematic. So one element per mark carries a
   translucent BODY and keeps its drawn edge on top — the mark becomes an object
   with a face, in one colour, with no gradient to muddy at small sizes.

   `body` goes on the shape holding the mark's mass, never on every shape: when
   a mark is two planes (AbStructure) filling only the near one is what creates
   the depth. `node` fills the small circles solid — a 1.2px ring at 20px is
   mush, and a dot is a dot. Both keep the stroke, so the footprint is unchanged.

   Opacity is the one number to turn if the set wants more or less weight. It is
   deliberately low: on a tinted chip the body has to stay under the label
   beside it, not compete with it.

   `flat` on any mark drops the fills back to a pure outline (fill-opacity 0 on
   the root cascades into every child), for a surface where the duotone is too
   much — a dense table row, or an icon printed at 12px. */
const body = { fill: 'currentColor', fillOpacity: 0.15 } as const;
const node = { fill: 'currentColor' } as const;

/* ---------------------------------------------------------------- identity */

/** Two offset planes - a company, an entity, a structure. (was Building2) */
export const AbStructure: AbIcon = (p) => (
  <Ab {...p}><rect x="3.5" y="9" width="7.5" height="11.5" rx="1.6" {...body} /><rect x="11" y="3.5" width="9.5" height="17" rx="1.6" /></Ab>
);

/** A plane with a node and a rule - a credential, an ID, a licence. (was IdCard) */
export const AbCredential: AbIcon = (p) => (
  <Ab {...p}><rect x="3" y="5.5" width="18" height="13" rx="3" {...body} /><circle cx="8.6" cy="12" r="2.1" /><path d="M13.6 12h4.6" /></Ab>
);

/** A ring on a base - an imprint, a seal, PRO work. (was Stamp) */
export const AbImprint: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="8.6" r="4.6" {...body} /><rect x="4.8" y="16.2" width="14.4" height="4.3" rx="1.6" /></Ab>
);

/** Ring, core, two tails - recognition, an award, a distinction. (was Award) */
export const AbSeal: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="9.2" r="5.4" {...body} /><circle cx="12" cy="9.2" r="1.9" /><path d="M8.6 14.2 7.2 20.5 12 18l4.8 2.5-1.4-6.3" /></Ab>
);

/** A single node under an arc - one named person on your file. (was UserRound) */
export const AbPerson: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="7.8" r="3.6" {...body} /><path d="M5.8 19.6a6.2 6.2 0 0 1 12.4 0" /></Ab>
);

/** Three nodes, linked - a team, a network, a market. (was Users) */
export const AbCluster: AbIcon = (p) => (
  <Ab {...p}><circle cx="7" cy="7.8" r="3" {...body} /><circle cx="17" cy="7.8" r="3" {...body} /><circle cx="12" cy="17" r="3" {...body} /><path d="M10 7.8h4M9.4 9.6l1.3 4.7M14.6 9.6l-1.3 4.7" /></Ab>
);

/** Two interlocking hooks - a partnership, an introduction. (was Handshake) */
export const AbJoin: AbIcon = (p) => (
  <Ab {...p}><path d="M10.4 6.6a5.4 5.4 0 0 0 0 10.8h.8" /><path d="M13.6 17.4a5.4 5.4 0 0 0 0-10.8h-.8" /><path d="M9.2 12h5.6" /></Ab>
);

/* ------------------------------------------------------------------- trust */

/** Nested hexagons - protection, compliance, something held. (was ShieldCheck) */
export const AbAegis: AbIcon = (p) => (
  <Ab {...p}><path d="M12 3.3l7.6 4.3v8.8L12 20.7 4.4 16.4V7.6z" /><path d="M12 7.9l3.6 2v4.2L12 16.1l-3.6-2v-4.2z" {...body} /></Ab>
);

/** A mark breaking out of a ring - verified, filed, cleared. (was BadgeCheck) */
export const AbVerified: AbIcon = (p) => (
  <Ab {...p}><path d="M20.3 10.5A8.4 8.4 0 1 1 13.5 3.7" {...body} /><path d="M8.4 12.4 11 15l8.6-9.4" /></Ab>
);

/** Ring with a mark - done, included, confirmed. (was CheckCircle2) */
export const AbConfirm: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="12" r="8.4" {...body} /><path d="M8.4 12.2 11 14.8l4.7-5.3" /></Ab>
);

/** A dial set into a plane - a vault, a data room, held privately. (was Lock) */
export const AbSecure: AbIcon = (p) => (
  <Ab {...p}><rect x="3.6" y="4.4" width="16.8" height="15.2" rx="3.4" {...body} /><circle cx="12" cy="12" r="3.6" /><path d="M12 12l2.6-2.6" /></Ab>
);

/** Diamond, faceted - the premium tier, private clients. (was Gem) */
export const AbFacet: AbIcon = (p) => (
  <Ab {...p}><path d="M12 3.4 20.6 12 12 20.6 3.4 12z" {...body} /><path d="M7.7 12h8.6M12 7.7v8.6" /></Ab>
);

/* -------------------------------------------------------------- paper flow */

/** A plane with three rules - a document, a filing, a report. (was FileText) */
export const AbDoc: AbIcon = (p) => (
  <Ab {...p}><rect x="5" y="3.5" width="14" height="17" rx="2.4" {...body} /><path d="M8.8 9h6.4M8.8 12.4h6.4M8.8 15.8h3.6" /></Ab>
);

/** A ruled table - books, a ledger, a reconciliation. (was Receipt) */
export const AbLedger: AbIcon = (p) => (
  <Ab {...p}><rect x="4" y="4" width="16" height="16" rx="2.4" {...body} /><path d="M4 9.4h16M9.6 9.4V20" /></Ab>
);

/** A plane of nodes and rules - a checklist, an intake, a scope. (was ClipboardList) */
export const AbChecklist: AbIcon = (p) => (
  <Ab {...p}><circle cx="5.2" cy="6.8" r="1.6" {...node} /><path d="M9.8 6.8h9.4" /><circle cx="5.2" cy="12" r="1.6" {...node} /><path d="M9.8 12h9.4" /><circle cx="5.2" cy="17.2" r="1.6" {...node} /><path d="M9.8 17.2h6.2" /></Ab>
);

/** A curve over a baseline - a signature, an engagement, terms agreed. (was FileSignature) */
export const AbSign: AbIcon = (p) => (
  <Ab {...p}><path d="M4 16.4c3.4 0 4.1-9.4 7.1-9.4s2.7 6.2 5.8 6.2h3.1" /><path d="M4 20.5h16" /></Ab>
);

/** Four planes in quadrant - the numbers, a calculation, a quote. (was Calculator) */
export const AbGrid: AbIcon = (p) => (
  <Ab {...p}><rect x="4" y="4" width="7" height="7" rx="1.8" {...body} /><rect x="13" y="4" width="7" height="7" rx="1.8" /><rect x="4" y="13" width="7" height="7" rx="1.8" /><rect x="13" y="13" width="7" height="7" rx="1.8" {...body} /></Ab>
);

/** A rotated plane with a node - a price, a plan, a package. (was Tag) */
export const AbTag: AbIcon = (p) => (
  <Ab {...p}><path d="M4 4h7.4l8.6 8.6-7.4 7.4L4 11.4z" {...body} /><circle cx="8.6" cy="8.6" r="1.5" {...node} /></Ab>
);

/* -------------------------------------------------------------------- time */

/** A dial with one hand - turnaround, a window, a deadline. (was Clock / Clock3) */
export const AbTempo: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="12" r="8.4" {...body} /><path d="M12 12l4.4-2.4" /><circle cx="12" cy="12" r="1.15" {...node} /></Ab>
);

/** A plane with a dot row - a calendar, a filing date, a schedule. (was CalendarDays) */
export const AbCalendar: AbIcon = (p) => (
  <Ab {...p}><rect x="3.5" y="5.2" width="17" height="15.3" rx="2.6" {...body} /><path d="M3.5 10.2h17" /><circle cx="8.4" cy="14.4" r="1.15" {...node} /><circle cx="12" cy="14.4" r="1.15" {...node} /><circle cx="15.6" cy="14.4" r="1.15" {...node} /></Ab>
);

/** Two arcs closing a loop with nodes - renewals, a recurring cycle. (was RefreshCw) */
export const AbCycle: AbIcon = (p) => (
  <Ab {...p}><path d="M20.2 12A8.2 8.2 0 0 1 6.2 17.8" /><path d="M3.8 12A8.2 8.2 0 0 1 17.8 6.2" /><circle cx="20.2" cy="12" r="1.15" {...node} /><circle cx="3.8" cy="12" r="1.15" {...node} /></Ab>
);

/** A core inside two rings of arcs - a reminder, an alert, a nudge. (was BellRing) */
export const AbPing: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="12" r="2.1" {...node} /><path d="M16.4 7.6a6.2 6.2 0 0 1 0 8.8" /><path d="M7.6 16.4a6.2 6.2 0 0 1 0-8.8" /><path d="M19.2 4.8a10.2 10.2 0 0 1 0 14.4" /><path d="M4.8 19.2a10.2 10.2 0 0 1 0-14.4" /></Ab>
);

/* ------------------------------------------------------------- performance */

/** A rising path ending in a node - growth, a raise, a trajectory. (was TrendingUp) */
export const AbAscend: AbIcon = (p) => (
  <Ab {...p}><path d="M3.8 17.6 9.4 12l3.4 3.2L19.6 8" /><circle cx="19.9" cy="7.7" r="1.5" {...node} /></Ab>
);

/** Three columns on a baseline - metrics, KPIs, reporting. (was BarChart3) */
export const AbBars: AbIcon = (p) => (
  <Ab {...p}><path d="M3.6 20.4h16.8" /><path d="M7.2 20.4v-5.6M12 20.4V8.4M16.8 20.4v-8.4" /><path d="M14.6 12h4.4v8.4h-4.4z" {...body} stroke="none" /></Ab>
);

/** Concentric rings on a core - a target, a score, a mandate. (was Target) */
export const AbAim: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="12" r="8.4" /><circle cx="12" cy="12" r="4.4" {...body} /><circle cx="12" cy="12" r="0.9" {...node} /></Ab>
);

/** A plane on a stem - a board, a review, a presentation. (was Presentation) */
export const AbBoard: AbIcon = (p) => (
  <Ab {...p}><rect x="3.5" y="3.8" width="17" height="11.8" rx="2.2" {...body} /><path d="M12 15.6v4.6M8.6 20.2h6.8" /></Ab>
);

/** Two sliders - tuning, an adjustment, controls. (was Settings2) */
export const AbTuner: AbIcon = (p) => (
  <Ab {...p}><path d="M3.8 8.4h4.4M13.2 8.4h7" /><path d="M3.8 15.6h7.4M16.2 15.6h4" /><circle cx="10.7" cy="8.4" r="2.2" {...body} /><circle cx="13.7" cy="15.6" r="2.2" {...body} /></Ab>
);

/* ------------------------------------------------------------ capital & ops */

/** A node astride a plane - payroll, a wallet, funds moving. (was Wallet) */
export const AbPurse: AbIcon = (p) => (
  <Ab {...p}><rect x="3.5" y="6.8" width="15" height="11.4" rx="3" {...body} /><circle cx="18.5" cy="12.5" r="2.5" {...node} /></Ab>
);

/** A rule over two dishes - balance, law, a fair split. (was Scale) */
export const AbBalance: AbIcon = (p) => (
  <Ab {...p}><path d="M12 4v16.4M6.4 20.4h11.2" /><path d="M4 8.4h16" /><path d="M6.4 8.4v2.2M17.6 8.4v2.2" /><circle cx="6.4" cy="13" r="2.4" {...body} /><circle cx="17.6" cy="13" r="2.4" {...body} /></Ab>
);

/** A colonnade - an institution, a bank, a regulator. (was Landmark) */
export const AbColumns: AbIcon = (p) => (
  <Ab {...p}><path d="M3.6 8.6h16.8" /><path d="M6.8 8.6v9M12 8.6v9M17.2 8.6v9" /><path d="M15.9 8.6h2.6v9h-2.6z" {...body} stroke="none" /><path d="M4.6 20.4h14.8" /></Ab>
);

/** A plane with a rule and a lip - an engagement, a practice, a case. (was Briefcase) */
export const AbCase: AbIcon = (p) => (
  <Ab {...p}><rect x="3.5" y="7.4" width="17" height="12.6" rx="2.6" {...body} /><path d="M9.2 7.4V5.5h5.6v1.9" /><path d="M3.5 13h17" /></Ab>
);

/** A plane with an arc - commerce, a basket, a transaction. (was ShoppingCart) */
export const AbCommerce: AbIcon = (p) => (
  <Ab {...p}><rect x="3.8" y="8.4" width="16.4" height="11.8" rx="2.8" {...body} /><path d="M8.4 12a3.6 3.6 0 0 0 7.2 0" /></Ab>
);

/** Two opposed rules - an exchange, a transfer, a restructure. (was ArrowLeftRight) */
export const AbExchange: AbIcon = (p) => (
  <Ab {...p}><path d="M4 9.2h13.4l-3.6-3.6" /><path d="M20 14.8H6.6l3.6 3.6" /></Ab>
);

/* ---------------------------------------------------------- reach & motion */

/** Quarter arcs from a node - outreach, a call, a first contact. (was PhoneCall) */
export const AbSignal: AbIcon = (p) => (
  <Ab {...p}><circle cx="5" cy="19" r="1.6" {...node} /><path d="M5 13.4A5.6 5.6 0 0 1 10.6 19" /><path d="M5 8.2A10.8 10.8 0 0 1 15.8 19" /><path d="M5 3.6A15.4 15.4 0 0 1 20.4 19" /></Ab>
);

/** A core emitting arcs - broadcast, an audience, a channel. (was Youtube) */
export const AbBroadcast: AbIcon = (p) => (
  <Ab {...p}><circle cx="8" cy="12" r="2.1" {...node} /><path d="M12.6 8.2a5.6 5.6 0 0 1 0 7.6" /><path d="M15.8 5.4a9.8 9.8 0 0 1 0 13.2" /></Ab>
);

/** A meshed ring - cross-border, global reach, jurisdictions. (was Globe2) */
export const AbGlobeMesh: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="12" r="8.4" {...body} /><path d="M3.6 12h16.8" /><ellipse cx="12" cy="12" rx="3.9" ry="8.4" /></Ab>
);

/** Two rings, interlocked - a connection, a link, an integration. (was Link2) */
export const AbLink: AbIcon = (p) => (
  <Ab {...p}><circle cx="8.6" cy="12" r="4.8" {...body} /><circle cx="15.4" cy="12" r="4.8" /></Ab>
);

/** A rising apex over thrust - a launch, a sprint, momentum. (was Rocket) */
export const AbLaunch: AbIcon = (p) => (
  <Ab {...p}><path d="M12 3.4 16.6 11.6H7.4z" {...body} /><path d="M9.2 14.8h5.6M10.6 18.2h2.8" /></Ab>
);

/** A four-ray star on a core - new, AI, a spark of insight. (was Sparkles) */
export const AbSpark: AbIcon = (p) => (
  <Ab {...p}><path d="M12 3.6v5.6M12 14.8v5.6M3.6 12h5.6M14.8 12h5.6" /><circle cx="12" cy="12" r="1.5" {...node} /></Ab>
);

/** A stepped pulse - energy, speed, a surge. (was Zap) */
export const AbSurge: AbIcon = (p) => (
  <Ab {...p}><path d="M3.6 15.6 8 9.4l3.6 3.8L15.4 6l5 8.4" /></Ab>
);

/** A needle in a ring - direction, strategy, a bearing. (was Compass) */
export const AbBearing: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="12" r="8.4" /><path d="M15.6 8.4 10.4 10.4 8.4 15.6l5.2-2z" {...body} /></Ab>
);

/* ----------------------------------------------------------- build & craft */

/** An open jaw on a shaft - a tool, an implementation, a fix. (was Wrench) */
export const AbAdjust: AbIcon = (p) => (
  <Ab {...p}><path d="M17.7 6.3A4.4 4.4 0 1 1 11.5 6.3" {...body} /><path d="M11.5 12.5 4.6 19.4" /></Ab>
);

/** Nested planes with ticks - a core system, compute, technology. (was Cpu) */
export const AbCore: AbIcon = (p) => (
  <Ab {...p}><rect x="6.6" y="6.6" width="10.8" height="10.8" rx="2.4" /><rect x="10.2" y="10.2" width="3.6" height="3.6" rx="1" {...body} /><path d="M9.6 3.6v3M14.4 3.6v3M9.6 17.4v3M14.4 17.4v3M3.6 9.6h3M3.6 14.4h3M17.4 9.6h3M17.4 14.4h3" /></Ab>
);

/** Stacked strata - data, a store, a source of record. (was Database) */
export const AbStack: AbIcon = (p) => (
  <Ab {...p}><ellipse cx="12" cy="6.8" rx="7.4" ry="2.8" {...body} /><path d="M4.6 12c0 1.5 3.3 2.8 7.4 2.8s7.4-1.3 7.4-2.8" /><path d="M4.6 6.8v10.4c0 1.5 3.3 2.8 7.4 2.8s7.4-1.3 7.4-2.8V6.8" /></Ab>
);

/** Three planes in depth - layers, a stack of services, a portfolio. (was Layers) */
export const AbLayers: AbIcon = (p) => (
  <Ab {...p}><path d="M12 3.4 20.4 8 12 12.6 3.6 8z" {...body} /><path d="M3.6 12.4 12 17l8.4-4.6" /><path d="M3.6 16.6 12 21.2l8.4-4.6" /></Ab>
);

/** A closed volume - a package, a deliverable, something shipped. (was PackageCheck) */
export const AbParcel: AbIcon = (p) => (
  <Ab {...p}><path d="M12 3.4 20 8v8l-8 4.6L4 16V8z" /><path d="M12 3.4 20 8v4.6L12 17.2z" {...body} stroke="none" /><path d="M4 8l8 4.6L20 8M12 12.6v8" /></Ab>
);

/** Mirrored chevrons on a core - code, software, engineering. (was Code2) */
export const AbBrackets: AbIcon = (p) => (
  <Ab {...p}><path d="M8.4 7.2 3.8 12l4.6 4.8M15.6 7.2 20.2 12l-4.6 4.8" /><circle cx="12" cy="12" r="1.15" {...node} /></Ab>
);

/* ----------------------------------------------------------- voice & craft */

/** Two overlapping planes - a conversation, chat, an answer. (was MessageSquare) */
export const AbDialogue: AbIcon = (p) => (
  <Ab {...p}><rect x="3.4" y="4.4" width="12.2" height="10" rx="3" /><rect x="8.4" y="9.6" width="12.2" height="10" rx="3" {...body} /></Ab>
);

/** A lens - media, video, a portfolio of images. (was Camera / Youtube) */
export const AbLens: AbIcon = (p) => (
  <Ab {...p}><circle cx="12" cy="12" r="8.4" /><circle cx="12" cy="12" r="3" {...body} /><path d="M12 3.6v4.2M12 16.2v4.2" /></Ab>
);

/** Three overlapping fields - design, a palette, a creative practice. (was Palette) */
export const AbSpectrum: AbIcon = (p) => (
  <Ab {...p}><circle cx="9.4" cy="9.6" r="4.9" /><circle cx="14.6" cy="9.6" r="4.9" /><circle cx="12" cy="14.4" r="4.9" {...body} /></Ab>
);

/** A curve ending in a node - writing, authorship, a draft. (was PenLine) */
export const AbStroke: AbIcon = (p) => (
  <Ab {...p}><path d="M3.8 20.2c4.4-1 7-3.4 9-6.6s2.2-5.2 2.2-5.2" /><circle cx="17.6" cy="6.3" r="2.3" {...node} /></Ab>
);

/** An apex over two rules - teaching, a course, expertise shared. (was GraduationCap) */
export const AbApex: AbIcon = (p) => (
  <Ab {...p}><path d="M12 3.8 20 10H4z" {...body} /><path d="M7 14h10M9.4 18h5.2" /></Ab>
);
